import * as React from "react";
import { forwardRef } from "react";
import { styled } from "@mui/material/styles";
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from "@mui/material/TextField";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
  FONT_SIZES,
} from "../../constants/styles";

// MultilineTextField 상태 타입
export type MultilineTextFieldState = "enabled" | "disabled" | "error";

/**
 * 다행 텍스트 입력 컴포넌트의 props. MUI `TextField` 의 `multiline` 변형 래퍼.
 *
 * @example
 * ```tsx
 * <MultilineTextField label="설명" placeholder="내용을 입력하세요" minRows={3} maxRows={8} />
 * ```
 */
export interface MultilineTextFieldProps
  extends Omit<
    MuiTextFieldProps,
    | "size"
    | "variant"
    | "error"
    | "disabled"
    | "label"
    | "placeholder"
    | "helperText"
    | "multiline"
  > {
  /** 상태 토큰 — `disabled`/`error` 와 동등 효과 (기본 `"enabled"`) */
  state?: MultilineTextFieldState;
  label?: string;
  placeholder?: string;
  helperText?: string;
  showLabel?: boolean;
  showHelperText?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  /** 최소 행 수 (자동 확장 하한) */
  minRows?: number;
  /** 최대 행 수 (자동 확장 상한 — 초과 시 스크롤) */
  maxRows?: number;
  /** 행 수 고정 (`minRows`/`maxRows` 와 동시 사용 금지) */
  rows?: number;
  /**
   * 테스트 앵커. 래퍼가 아니라 **실제 textarea 노드**로 라우팅된다(`slotProps.htmlInput`).
   * `getByTestId().fill()` 이 textarea 에 직접 입력되어 조용한 실패를 막는다(design Decision 8).
   */
  "data-testid"?: string;
}

// 상태별 스타일 함수
const getMultilineTextFieldStateStyles = (state: MultilineTextFieldState) => {
  switch (state) {
    case "enabled":
      return {
        borderColor: COLOR_CONSTANTS.COLOR.GREY[300],
        labelColor: COLOR_CONSTANTS.text.secondary,
        textColor: COLOR_CONSTANTS.text.primary,
        placeholderColor: COLOR_CONSTANTS.text.disabled,
        backgroundColor: "transparent",
      };
    case "disabled":
      return {
        borderColor: COLOR_CONSTANTS.COLOR.GREY[300],
        labelColor: COLOR_CONSTANTS.text.disabled,
        textColor: COLOR_CONSTANTS.text.disabled,
        placeholderColor: COLOR_CONSTANTS.text.disabled,
        backgroundColor: COLOR_CONSTANTS.COLOR.GREY[50],
      };
    case "error":
      return {
        borderColor: COLOR_CONSTANTS.error.main,
        labelColor: COLOR_CONSTANTS.error.main,
        textColor: COLOR_CONSTANTS.text.primary,
        placeholderColor: COLOR_CONSTANTS.text.disabled,
        backgroundColor: "transparent",
      };
    default:
      return {
        borderColor: COLOR_CONSTANTS.COLOR.GREY[300],
        labelColor: COLOR_CONSTANTS.text.secondary,
        textColor: COLOR_CONSTANTS.text.primary,
        placeholderColor: COLOR_CONSTANTS.text.disabled,
        backgroundColor: "transparent",
      };
  }
};

// 스타일드 MultilineTextField 컴포넌트
const StyledMultilineTextField = styled(MuiTextField, {
  shouldForwardProp: (prop) =>
    prop !== "state" && prop !== "showLabel",
})<
  MultilineTextFieldProps & {
    state: MultilineTextFieldState;
    showLabel: boolean;
  }
>(({ theme, state, showLabel }) => {
  const stateStyles = getMultilineTextFieldStateStyles(state);

  return {
    // 컨테이너 스타일
    "& .MuiFormControl-root": {
      width: "100%",
    },

    // Input 스타일
    "& .MuiOutlinedInput-root": {
      padding: `${SPACING_CONSTANTS.spacing[2]}px`,
      borderRadius: SPACING_CONSTANTS.borderRadius.sm,
      backgroundColor: stateStyles.backgroundColor,
      transition: "all 0.2s ease-in-out",

      "& fieldset": {
        borderColor: stateStyles.borderColor,
        borderWidth: "1px",
      },

      // 호버 상태 - enabled 상태에서만 호버 효과 적용
      "&:hover fieldset": {
        borderColor:
          state === "enabled"
            ? COLOR_CONSTANTS.text.primary
            : stateStyles.borderColor,
        borderWidth: "1px",
      },

      // 포커스 상태 - enabled 상태에서만 포커스 효과 적용
      "&.Mui-focused fieldset": {
        borderColor:
          state === "enabled"
            ? COLOR_CONSTANTS.primary.main
            : stateStyles.borderColor,
        borderWidth: "1px",
      },

      // 비활성화 상태
      "&.Mui-disabled fieldset": {
        borderColor: stateStyles.borderColor,
        borderWidth: "1px",
      },

      // Input 텍스트 스타일
      "& .MuiOutlinedInput-input": {
        // padding: "8px 12px",
        fontSize: FONT_SIZES.md,
        fontFamily: "Pretendard, sans-serif",
        fontWeight: 400,
        lineHeight: "24px",
        letterSpacing: "0.15px",
        color: stateStyles.textColor,

        "&::placeholder": {
          color: stateStyles.placeholderColor,
          opacity: 1,
        },
      },

      // Adornment 스타일
      "& .MuiInputAdornment-root": {
        "& .MuiSvgIcon-root": {
          fontSize: FONT_SIZES.xxl,
          color: stateStyles.textColor,
        },
      },
    },

    // 라벨 스타일
    "& .MuiInputLabel-root": {
      fontSize: FONT_SIZES.sm,
      lineHeight: "12px",
      letterSpacing: "0.15px",
      fontFamily: "Pretendard, sans-serif",
      fontWeight: 400,
      color: stateStyles.labelColor,
      transform: `translate(${SPACING_CONSTANTS.spacing[1]}px, -6px) scale(1)`, // 항상 위쪽 고정, 왼쪽으로 이동
      transition: "all 0.2s ease-in-out",
      backgroundColor: "white", // 배경색으로 outline과 겹침 방지

      // 호버 상태에서 라벨 색상 변경 (enabled 상태에서만)
      "&:hover": {
        color:
          state === "enabled"
            ? COLOR_CONSTANTS.text.primary
            : stateStyles.labelColor,
      },

      // 포커스 상태에서 라벨 색상 변경 (enabled 상태에서만)
      "&.Mui-focused": {
        color:
          state === "enabled"
            ? COLOR_CONSTANTS.primary.main
            : stateStyles.labelColor,
        transform: `translate(${SPACING_CONSTANTS.spacing[1]}px, -6px) scale(1)`,
      },

      "&.MuiInputLabel-shrink": {
        transform: `translate(${SPACING_CONSTANTS.spacing[1]}px, -6px) scale(1)`,
      },
    },

    // Helper Text 스타일
    "& .MuiFormHelperText-root": {
      fontSize: FONT_SIZES.sm,
      fontFamily: "Roboto, sans-serif",
      fontWeight: 400,
      lineHeight: "20px",
      letterSpacing: "0.4px",
      color:
        state === "error"
          ? COLOR_CONSTANTS.error.main
          : COLOR_CONSTANTS.text.secondary,
      marginTop: `${SPACING_CONSTANTS.spacing[1]}px`,
    },
  };
});

export const MultilineTextField = forwardRef<
  HTMLDivElement,
  MultilineTextFieldProps
>(
  (
    {
      state = "enabled",
      label,
      placeholder,
      helperText,
      showLabel = true,
      showHelperText = false,
      startIcon,
      endIcon,
      disabled,
      error,
      minRows,
      maxRows,
      rows,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    // 상태 결정 로직
    const isDisabled = disabled || state === "disabled";
    const hasError = error || state === "error";

    // InputProps 설정
    const inputProps = {
      startAdornment: startIcon,
      endAdornment: endIcon,
    };

    // Multiline 설정
    const multilineProps = {
      multiline: true,
      minRows: minRows,
      maxRows: maxRows,
      rows: rows,
    };

    return (
      <StyledMultilineTextField
        ref={ref}
        state={state}
        showLabel={showLabel}
        variant="outlined"
        label={showLabel ? label : undefined}
        placeholder={placeholder}
        helperText={showHelperText ? helperText : undefined}
        disabled={isDisabled}
        error={hasError}
        slotProps={{
          input: inputProps,
          // testid 를 textarea 노드까지 라우팅 — getByTestId().fill() 이 직관적으로 동작.
          ...(dataTestId ? { htmlInput: { "data-testid": dataTestId } } : {}),
          inputLabel: {
            shrink: true, // 라벨이 항상 위쪽에 고정되도록 설정
            ...props.InputLabelProps,
          },
        }}
        {...multilineProps}
        {...props}
      />
    );
  }
);

MultilineTextField.displayName = "MultilineTextField";
